import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, subirBinario } from '../lib/api';
import { Spinner } from '../components/Spinner';
import { useConfirm } from '../components/ConfirmDialog';
import ChatAndres from './ChatAndres';
import { CasosReferencia } from './dia/CasosReferencia';
import type { Favorito } from './dia/tipos';
import { Ayuda } from '../components/Ayuda';

/**
 * Entrenar a Andrés (2026-07-20) — página dedicada, no modal. Dos pilares:
 *   1. Su OFICIO (rol, criterio, límites) versionado: guardar = nueva versión activa.
 *   2. Su BIBLIOTECA (conocimiento transversal que vive SOLO en la app, no en SharePoint):
 *      file explorer con subida por drag & drop; cada archivo subido lo ESTUDIA el worker
 *      (extracción de texto) y pasa a formar parte de su conocimiento permanente.
 */

interface DocBiblioteca {
  id: string;
  nombre: string;
  extension: string | null;
  size: number | null;
  estado: 'pendiente' | 'listo' | 'sin_texto' | 'error' | string;
  error: string | null;
  ingestado_at: string | null;
}

interface VersionOficio { id: string; version: number; estado: string; nota: string | null; created_at: string }

const kb = (n: number | null) => (n === null ? '' : n >= 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const ICONO_EXT: Record<string, string> = {
  pdf: '📕', docx: '📘', doc: '📘', xlsx: '📗', xls: '📗', pptx: '📙',
  html: '🌐', htm: '🌐', txt: '📄', md: '📄', csv: '📗', zip: '🗂️', kmz: '🗺️', kml: '🗺️', dbf: '🗺️',
};

function ChipEstado({ d }: { d: DocBiblioteca }) {
  if (d.estado === 'listo') return <span className="chip bg-emerald-50 text-emerald-700 ring-emerald-300">Estudiado ✓</span>;
  if (d.estado === 'pendiente') return <span className="chip bg-celeste/20 text-azul-profundo ring-celeste/60">Estudiando…</span>;
  if (d.estado === 'sin_texto') return <span className="chip bg-amber-50 text-amber-700 ring-amber-300" title="Formato sin texto extraíble aún: queda catalogado por nombre">Sin texto</span>;
  return <span className="chip bg-red-50 text-red-700 ring-red-300" title={d.error ?? ''}>Error</span>;
}

export default function EntrenarAndres() {
  const confirmar = useConfirm();
  // ── Oficio ──
  const [oficio, setOficio] = useState('');
  const [notaOficio, setNotaOficio] = useState('');
  const [versiones, setVersiones] = useState<VersionOficio[]>([]);
  const [cargandoOficio, setCargandoOficio] = useState(true);
  const [guardandoOficio, setGuardandoOficio] = useState(false);
  const [oficioMsg, setOficioMsg] = useState<string | null>(null);
  const [internet, setInternet] = useState<boolean | null>(null);
  // ── Biblioteca ──
  const [docs, setDocs] = useState<DocBiblioteca[]>([]);
  const [bibliotecaId, setBibliotecaId] = useState<string | null>(null);
  const [chatBiblioteca, setChatBiblioteca] = useState(false);
  const [chatRef, setChatRef] = useState<Favorito | null>(null);
  const [cargandoDocs, setCargandoDocs] = useState(true);
  const [subiendo, setSubiendo] = useState<string | null>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const cargarOficio = useCallback(async () => {
    try {
      const d = await apiFetch<{ agentes: { agent_code: string; context_brief: string | null; versiones: VersionOficio[] }[] }>('/api/entrenamiento');
      const a1 = d.agentes.find((a) => a.agent_code === 'A1');
      setOficio(a1?.context_brief ?? '');
      setVersiones(a1?.versiones ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el oficio.');
    } finally {
      setCargandoOficio(false);
    }
  }, []);

  const cargarDocs = useCallback(async () => {
    try {
      const d = await apiFetch<{ favorito: { id: string }; documentos: DocBiblioteca[] }>('/api/dia/biblioteca');
      setDocs(d.documentos);
      setBibliotecaId(d.favorito.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la biblioteca.');
    } finally {
      setCargandoDocs(false);
    }
  }, []);

  useEffect(() => {
    void cargarOficio();
    void cargarDocs();
    void apiFetch<{ internet: boolean }>('/api/dia/andres/internet').then((d) => setInternet(d.internet)).catch(() => undefined);
  }, [cargarOficio, cargarDocs]);

  async function cambiarInternet() {
    if (internet === null) return;
    const nuevo = !internet;
    setInternet(nuevo);
    try {
      await apiFetch('/api/dia/andres/internet', { method: 'PUT', body: JSON.stringify({ internet: nuevo }) });
    } catch (e) {
      setInternet(!nuevo);
      setError(e instanceof Error ? e.message : 'No se pudo cambiar el acceso a internet.');
    }
  }

  // Mientras Andrés estudia algo, la lista se refresca sola.
  const estudiando = docs.some((d) => d.estado === 'pendiente');
  useEffect(() => {
    if (!estudiando) return;
    const t = window.setInterval(() => void cargarDocs(), 3000);
    return () => window.clearInterval(t);
  }, [estudiando, cargarDocs]);

  async function guardarOficio() {
    setGuardandoOficio(true);
    setOficioMsg(null);
    try {
      const v = await apiFetch<{ version: { id: string; version: number } }>('/api/entrenamiento/versiones', {
        method: 'POST',
        body: JSON.stringify({ agent_code: 'A1', tipo: 'brief', contenido: oficio, nota: notaOficio.trim() || null }),
      });
      await apiFetch(`/api/entrenamiento/versiones/${v.version.id}/activar`, { method: 'POST', body: JSON.stringify({ confirmar: true }) });
      setOficioMsg(`v${v.version.version} activa — la próxima conversación de Andrés la usará.`);
      setNotaOficio('');
      void cargarOficio();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar el oficio.');
    } finally {
      setGuardandoOficio(false);
    }
  }

  async function subirArchivos(lista: FileList | File[]) {
    setError(null);
    const archivos = Array.from(lista);
    for (const f of archivos) {
      setSubiendo(f.name);
      try {
        await subirBinario(`/api/dia/biblioteca/documentos?nombre=${encodeURIComponent(f.name)}`, f);
      } catch (e) {
        setError(`${f.name}: ${e instanceof Error ? e.message : 'no se pudo subir'}`);
        break;
      }
    }
    setSubiendo(null);
    await cargarDocs();
  }

  async function quitarDoc(d: DocBiblioteca) {
    if (!(await confirmar({ titulo: 'Sacar del conocimiento', mensaje: `¿Sacar "${d.nombre}" del conocimiento de Andrés?`, textoAceptar: 'Sacar', destructivo: true }))) return;
    try {
      await apiFetch(`/api/dia/biblioteca/documentos/${d.id}`, { method: 'DELETE' });
      await cargarDocs();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo eliminar.');
    }
  }

  if (chatRef) return <ChatAndres favoritoId={chatRef.id} nombre={chatRef.nombre} onVolver={() => setChatRef(null)} />;
  if (chatBiblioteca && bibliotecaId) {
    return <ChatAndres favoritoId={bibliotecaId} nombre="Biblioteca AMBEC (transversal)" onVolver={() => setChatBiblioteca(false)} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/agentes/dia" className="btn-fantasma !h-8 !px-3 !text-[13px]">← Volver</Link>
          <div>
            <p className="flex items-center gap-1.5 text-[13px] font-normal text-gris">Lo que Andrés sabe para TODAS las DIA: su biblioteca, los casos de referencia y su oficio.<Ayuda texto="Aquí no se trabaja ningún proyecto: todo lo que subas o marques aquí entrena a Andrés y a los redactores para todas las DIA. Los proyectos en curso viven en Agente DIA." /></p>
          </div>
        </div>
        <button type="button" onClick={() => setChatBiblioteca(true)} disabled={!bibliotecaId} className="btn-secundario shrink-0 disabled:opacity-50" title="Pregúntale a Andrés sobre la normativa y guías de su biblioteca">
          💬 Conversar sobre la Biblioteca
        </button>
      </div>

      {error && <p role="alert" className="alerta-error">{error}</p>}

      {/* ── Biblioteca: file explorer con subida ── */}
      <section
        className="tarjeta p-5"
        onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={(e) => { e.preventDefault(); setArrastrando(false); void subirArchivos(e.dataTransfer.files); }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-azul-noche">
            Biblioteca AMBEC <span className="font-normal text-gris">· conocimiento transversal ( {docs.length} )</span>
          </h2>
          <button type="button" onClick={() => inputRef.current?.click()} className="btn-secundario !h-8 !px-3 !text-[13px]" disabled={subiendo !== null}>
            + Subir documentos
          </button>
          <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) void subirArchivos(e.target.files); e.target.value = ''; }} />
        </div>

        <div
          className={`mt-3 rounded-xl border-2 border-dashed px-4 py-5 text-center text-sm transition-colors ${
            arrastrando ? 'border-azul-profundo bg-celeste/15 text-azul-profundo' : 'border-slate-200 text-gris'
          }`}
        >
          {subiendo ? (
            <span className="inline-flex items-center gap-2 font-normal"><Spinner tamano="xs" /> Subiendo {subiendo}…</span>
          ) : (
            <span className="font-light">Arrastra aquí guías, reglamentos, plantillas, RCAs… (PDF, Word, Excel, HTML y más) o usa "Subir documentos".</span>
          )}
        </div>

        {estudiando && (
          <p role="status" className="mt-3 rounded-xl border border-celeste/60 bg-[#F2F9FD] px-4 py-2.5 text-sm font-normal text-azul-noche">
            🔄 Andrés está estudiando los documentos nuevos — en cuanto cada uno diga "Estudiado ✓" ya es parte de su conocimiento.
          </p>
        )}

        {cargandoDocs ? (
          <div className="flex justify-center py-8"><Spinner tamano="md" /></div>
        ) : docs.length === 0 ? (
          <p className="mt-4 py-4 text-center text-sm font-light text-gris">Biblioteca vacía — sube el primer documento para empezar a entrenarlo.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center gap-2.5 py-2">
                <span aria-hidden="true" className="shrink-0 text-base">{ICONO_EXT[d.extension ?? ''] ?? '📄'}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-normal text-texto" title={d.nombre}>{d.nombre}</span>
                  <span className="block text-[11px] font-light text-gris">
                    {kb(d.size)}{d.ingestado_at ? ` · estudiado el ${new Date(d.ingestado_at).toLocaleDateString('es-CL')}` : ''}
                  </span>
                </span>
                <ChipEstado d={d} />
                <button
                  type="button"
                  onClick={() => void quitarDoc(d)}
                  className="shrink-0 rounded px-1.5 py-1 text-xs font-normal text-gris hover:text-red-600"
                  aria-label={`Quitar ${d.nombre}`}
                  title="Quitar del conocimiento"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <CasosReferencia onChat={setChatRef} />

      {/* ── Oficio versionado ── */}
      <section className="tarjeta p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-azul-noche">Su oficio <span className="font-normal text-gris">· rol, criterio y límites</span></h2>
          <button
            type="button"
            role="switch"
            aria-checked={internet === true}
            onClick={() => void cambiarInternet()}
            disabled={internet === null}
            title="Con internet, Andrés puede buscar y leer páginas web COMO COMPLEMENTO: los documentos mandan y toda fuente web se cita con su URL"
            className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-[12px] font-medium ring-1 ring-inset transition-colors ${
              internet ? 'bg-emerald-50 text-emerald-700 ring-emerald-300' : 'bg-slate-50 text-gris ring-slate-200'
            } disabled:opacity-50`}
          >
            <span aria-hidden="true" className={`flex h-4 w-7 items-center rounded-full p-0.5 transition-colors ${internet ? 'justify-end bg-emerald-500' : 'justify-start bg-slate-300'}`}>
              <span className="h-3 w-3 rounded-full bg-white shadow" />
            </span>
            🌐 Internet {internet ? 'habilitado' : 'apagado'}
          </button>
        </div>
        <p className="mt-1 text-xs font-normal leading-relaxed text-gris">
          Al guardar se crea una versión y queda activa al tiro: la próxima conversación la usa. El historial queda versionado — nada se pierde.
        </p>
        {cargandoOficio ? (
          <div className="flex justify-center py-8"><Spinner tamano="md" /></div>
        ) : (
          <>
            {oficioMsg && <p role="status" className="mt-3 rounded-lg bg-emerald-50/70 px-3 py-2 text-sm text-emerald-900 ring-1 ring-inset ring-emerald-200">✓ {oficioMsg}</p>}
            <label className="etiqueta mt-4" htmlFor="oficio-a1">Oficio de Andrés</label>
            <textarea id="oficio-a1" value={oficio} onChange={(e) => setOficio(e.target.value)} rows={14} className="campo mt-1 !h-auto max-h-[56vh] resize-y font-mono !text-[12.5px] leading-relaxed" />
            <label className="etiqueta mt-3" htmlFor="oficio-nota">Nota de la versión (qué cambiaste y por qué)</label>
            <input id="oficio-nota" value={notaOficio} onChange={(e) => setNotaOficio(e.target.value)} className="campo mt-1" placeholder="p. ej. Exigir siempre citar artículo del RSEIA en temas de PAS" />
            <div className="mt-4 flex items-center justify-between gap-3">
              {versiones.length > 0 ? (
                <details className="min-w-0">
                  <summary className="cursor-pointer text-xs font-medium text-azul-profundo">Historial ({versiones.length} versiones)</summary>
                  <ul className="mt-2 flex flex-col gap-1">
                    {versiones.slice(0, 12).map((v) => (
                      <li key={v.id} className="text-[11.5px] font-normal text-gris">
                        <span className="font-medium text-azul-noche">v{v.version}</span> · {v.estado} · {new Date(v.created_at).toLocaleDateString('es-CL')}{v.nota ? ` — ${v.nota}` : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : <span />}
              <button type="button" onClick={() => void guardarOficio()} disabled={guardandoOficio || !oficio.trim()} className="btn-primario shrink-0 disabled:opacity-50">
                {guardandoOficio ? 'Activando…' : 'Guardar y activar'}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
